import { Popover, PopoverAnchor, PopoverContent } from '@newfront-insurance/core-ui/v2';
import { useCallback, useState } from 'react';

import type { SkillCommand } from '../../../../lib/skill-constants';
import type { CustomSkillRecord } from '../../../../queries/chats';
import { CreateSkillView } from '../custom-skills-ui';
import { useSettingsPanelContextSafe } from '../settings-panel-context';
import { SkillList } from '../skill';

interface SkillMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (command: SkillCommand) => void;
  commands: SkillCommand[];
  searchQuery: string;
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
}

/**
 * Popover-based skill menu for desktop/wide panels.
 * Uses the shared SkillList with 'default' variant.
 */
export function SkillMenu({
  isOpen,
  onClose,
  onSelect,
  commands,
  searchQuery,
  selectedIndex,
  onSelectedIndexChange,
}: SkillMenuProps) {
  const settingsPanel = useSettingsPanelContextSafe();
  const [isCreateViewOpen, setIsCreateViewOpen] = useState(false);
  const [skillToUpdate, setSkillToUpdate] = useState<CustomSkillRecord | null>(null);

  const handleAddSkill = useCallback(() => {
    onClose();
    if (settingsPanel) {
      settingsPanel.pushView({ type: 'custom-skill-create' });
      return;
    }

    setIsCreateViewOpen(true);
  }, [onClose, settingsPanel]);

  const handleEditSkill = useCallback(
    (skill: CustomSkillRecord) => {
      onClose();
      if (settingsPanel) {
        settingsPanel.pushView({ type: 'custom-skill-edit', skillUuid: skill.uuid });
        return;
      }

      setSkillToUpdate(skill);
    },
    [onClose, settingsPanel],
  );

  if (!isOpen && !isCreateViewOpen && !skillToUpdate) {
    return null;
  }

  return (
    <>
      {isOpen && commands.length > 0 ? (
        <Popover open={isOpen} onOpenChange={(open) => !open && onClose()}>
          <PopoverAnchor asChild>
            <span className="pointer-events-none absolute bottom-full left-0" />
          </PopoverAnchor>
          <PopoverContent
            className="border-steel-200 w-[548px] overflow-hidden rounded-sm border bg-white p-0 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.1)]"
            side="top"
            align="start"
            sideOffset={8}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            // Let our own keydown handler close the menu and prevent the
            // browser's default Esc-on-contenteditable blur behavior.
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <SkillList
              commands={commands}
              searchQuery={searchQuery}
              isOpen={isOpen}
              onSelect={onSelect}
              onClose={onClose}
              onAddSkill={handleAddSkill}
              onEditSkill={handleEditSkill}
              variant="default"
              showKeyboardHints
              selectedIndex={selectedIndex}
              onSelectedIndexChange={onSelectedIndexChange}
            />
          </PopoverContent>
        </Popover>
      ) : null}

      {isCreateViewOpen ? (
        <CreateSkillView onBack={() => setIsCreateViewOpen(false)} onChatStarted={() => setIsCreateViewOpen(false)} />
      ) : null}
      {skillToUpdate ? <CreateSkillView onBack={() => setSkillToUpdate(null)} skillToUpdate={skillToUpdate} /> : null}
    </>
  );
}

/**
 * Hook for managing skill menu state.
 * Use this with SkillMenu or SkillList.
 */
export function useSkillMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const open = useCallback((initialQuery = '/', initialSelectedIndex = 0) => {
    setSearchQuery(initialQuery);
    setSelectedIndex(initialSelectedIndex);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setSelectedIndex(0);
  }, []);

  const updateQuery = useCallback((query: string, nextSelectedIndex = 0) => {
    setSearchQuery(query);
    setSelectedIndex(nextSelectedIndex);
  }, []);

  return {
    isOpen,
    searchQuery,
    selectedIndex,
    open,
    close,
    updateQuery,
    setSelectedIndex,
  };
}
